import { useState } from "react";
import { Formik } from "formik";
import { useToasts } from "react-toast-notifications";
import * as Animate from "react-reveal";
import InputGroup from "../../../components/elements/InputGroup";
import SelectGroup from "../../../components/elements/SelectGroup";
import TextAreaGroup from "../../../components/elements/TextAreaGroup";
import { submitHiringRequestApi } from "../../../store/actions";
import "./HireRequestFormSection.css";
import { getErrorMessage } from "../../../helpers/errorMessage";
import { ArrowUpRight } from "../../../components/svgs";
import Avatar1 from "../../../assets/images/png/talent-leader.webp";
import Avatar2 from "../../../assets/images/png/avatars/6.webp";
import Avatar3 from "../../../assets/images/png/avatars/7.webp";

export const triggerError = (addToast, error) => {
  addToast(getErrorMessage(error), {
    appearance: "error",
    autoDismiss: true,
  });
};

const GOLD_TINT_BG = "rgba(251,175,27,0.06)";
const GOLD_TINT_BORDER = "rgba(251,175,27,0.35)";

const OneTimeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const OutsourcingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M4 12a8 8 0 0114-5.3M20 12a8 8 0 01-14 5.3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M18 4v4h-4M6 20v-4h4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ENGAGEMENT_TYPES = [
  {
    value: "ONE_TIME_HIRE",
    label: "One-time Hire",
    description:
      "Utiva sources and recommends qualified talent. Your company manages the employee directly after hiring.",
    icon: <OneTimeIcon />,
  },
  {
    value: "OUTSOURCING",
    label: "Outsourcing / Talent Management",
    description:
      "Utiva recruits, onboards and manages the talent, including ongoing support and agreed services.",
    icon: <OutsourcingIcon />,
  },
];

const NUMBER_OF_PEOPLE_OPTIONS = ["1", "2–5", "6–10", "10+"];

const EMPLOYMENT_TYPE_OPTIONS = [
  "Full-time",
  "Part-time",
  "Fixed-term Contract",
  "Independent Contractor",
  "Project-based",
  "Internship",
  "Temporary / Short-term",
  "Other",
];

const WORK_LOCATION_OPTIONS = [
  "Remote — anywhere",
  "Remote — specific region",
  "Hybrid",
  "On-site",
];

const START_DATE_OPTIONS = [
  "As soon as possible",
  "Within 30 days",
  "Within 3 months",
  "Just exploring",
];

const CONTRACT_DURATION_OPTIONS = [
  "1 Month",
  "3 Months",
  "6 Months",
  "12 Months",
  "Ongoing / Indefinite",
];

const YES_NO_OPTIONS = ["Yes", "No"];

const EQUIPMENT_REQUIREMENT_OPTIONS = [
  "Laptop needed",
  "Full workstation setup",
  "No equipment needed",
  "Other",
];

const CURRENCY_OPTIONS = [
  { value: "NGN", label: "NGN — Nigerian Naira" },
  { value: "USD", label: "USD — United States Dollar" },
];

const BENEFIT_OPTIONS = [
  "Health Insurance / HMO",
  "Pension",
  "Performance Bonus",
  "Annual Bonus",
  "Commission",
  "Equity or Stock Options",
  "Paid Annual Leave",
  "Paid Sick Leave",
  "Maternity or Paternity Leave",
  "Learning and Development Budget",
  "Internet or Data Allowance",
  "Transport Allowance",
  "Housing Allowance",
  "Meal Allowance",
  "Equipment or Work Tools",
  "Flexible Working Hours",
  "Remote Work Support",
  "Relocation Support",
  "Visa Sponsorship",
  "No additional benefits",
  "Other",
];

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const parseNumberOfPeople = (value) => {
  const match = String(value).match(/\d+/);
  return match ? Number(match[0]) : undefined;
};

const STEPS = [
  { key: "role", label: "Role" },
  { key: "team", label: "Team" },
  { key: "location", label: "Location" },
  { key: "budget", label: "Budget" },
  { key: "infrastructure", label: "Infrastructure" },
];

const STEP_FIELDS = [
  ["engagementType", "workEmail", "role", "employmentType"],
  ["numberOfPeople"],
  ["workLocation", "startDate"],
  ["currency", "minBudget", "maxBudget"],
  [],
];

const SectionHeading = ({ children, tone }) => (
  <div className="flex items-center gap-3 mb-1">
    <p
      className={`text-sm font-extrabold uppercase tracking-widest whitespace-nowrap ${
        tone === "secondary" ? "text-secondary" : "text-primary"
      }`}
    >
      {children}
    </p>
    <span className="flex-1 h-px bg-hire-line"></span>
  </div>
);

const FieldLabel = ({ children }) => (
  <p className="text-base font-bold text-default mb-2">{children}</p>
);

const RadioOption = ({
  value,
  label,
  description,
  icon,
  checked,
  onChange,
}) => (
  <label
    className={`flex items-start gap-4 p-4 rounded-2xl border cursor-pointer transition-all select-none ${
      checked
        ? "border-primary border-2 bg-primary-light"
        : "border-gray-2 hover:border-primary"
    }`}
  >
    <span
      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 text-secondary"
      style={{ backgroundColor: GOLD_TINT_BG }}
    >
      {icon}
    </span>
    <span className="flex-1">
      <span className="block text-lg font-bold text-primary">{label}</span>
      <span className="block text-sm text-gray mt-1 leading-relaxed">
        {description}
      </span>
    </span>
    {checked && <span className="text-primary font-bold mt-1">✓</span>}
    <input
      type="radio"
      name="engagementType"
      value={value}
      checked={checked}
      onChange={onChange}
      className="sr-only"
    />
  </label>
);

const CheckOption = ({ name, value, label, checked, onChange }) => (
  <label
    className={`flex items-center gap-2 pl-2 pr-4 py-2 rounded-full border cursor-pointer text-sm font-semibold transition-all select-none ${
      checked
        ? "border-primary bg-primary-light text-primary"
        : "border-gray-2 text-gray hover:border-primary"
    }`}
  >
    <span
      className={`w-5 h-5 rounded-full border flex-shrink-0 flex items-center justify-center text-sm transition-all ${
        checked
          ? "border-primary bg-primary text-white"
          : "border-gray-2 text-gray-2 bg-white"
      }`}
    >
      {checked ? "✓" : "+"}
    </span>
    <input
      type="checkbox"
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      className="sr-only"
    />
    {label}
  </label>
);

const CheckboxField = ({ name, checked, onChange, label }) => (
  <label
    className={`flex items-center gap-3 px-4 h-12 sm:h-12 md:h-14 lg:h-14 rounded-lg border cursor-pointer text-base font-semibold transition-all select-none ${
      checked
        ? "border-primary text-primary bg-primary-light"
        : "border-gray-2 text-gray"
    }`}
  >
    <span
      className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center transition-all ${
        checked ? "border-primary bg-primary" : "border-gray-2"
      }`}
    >
      {checked && (
        <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
          <path
            d="M1 3l2 2 4-4"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
    <input
      type="checkbox"
      name={name}
      checked={checked}
      onChange={onChange}
      className="sr-only"
    />
    {label}
  </label>
);

const MoneyInput = ({ symbol, error, ...props }) => (
  <>
    <div className="flex items-stretch w-full border border-gray-2 rounded-lg overflow-hidden mb-2 focus-within:border-primary bg-white">
      <span className="flex items-center px-3 sm:px-4 bg-hire-tint-blue text-primary font-bold text-base sm:text-lg border-r border-gray-2 flex-shrink-0">
        {symbol}
      </span>
      <input
        type="number"
        min="0"
        className="flex-1 min-w-0 px-3 sm:px-4 h-12 sm:h-12 md:h-14 lg:h-14 text-base sm:text-lg text-primary placeholder-gray-2 focus:outline-none"
        {...props}
      />
    </div>
    <span className="text-sm text-red-600">{error}</span>
  </>
);

const validate = (values) => {
  const errors = {};

  if (!values.engagementType) {
    errors.engagementType = "Select an engagement type";
  }

  if (!values.workEmail) {
    errors.workEmail = "Work email is required";
  } else if (!EMAIL_REGEX.test(values.workEmail)) {
    errors.workEmail = "Please enter a valid email address";
  }

  if (!values.role) {
    errors.role = "Role is required";
  }

  if (!values.numberOfPeople) {
    errors.numberOfPeople = "Select how many people";
  }

  if (!values.employmentType) {
    errors.employmentType = "Select an employment type";
  }

  if (!values.workLocation) {
    errors.workLocation = "Select a work location";
  }

  if (!values.startDate) {
    errors.startDate = "Let us know when you need them";
  }

  if (values.engagementType === "OUTSOURCING") {
    if (!values.contractDuration) {
      errors.contractDuration = "Select a contract duration";
    }
    if (!values.expectedStartDate) {
      errors.expectedStartDate = "Expected start date is required";
    }
    if (!values.payrollSupport) {
      errors.payrollSupport = "Select an option";
    }
    if (!values.numberOfTalentsToBeManaged) {
      errors.numberOfTalentsToBeManaged = "Number of talents is required";
    }
  }

  if (!values.currency) {
    errors.currency = "Select a currency";
  }

  if (!values.minBudget) {
    errors.minBudget = "Minimum budget is required";
  }

  if (!values.maxBudget) {
    errors.maxBudget = "Maximum budget is required";
  }

  return errors;
};

const stepHasErrors = (stepIndex, values, errors) => {
  const fields = [...STEP_FIELDS[stepIndex]];
  if (stepIndex === 4 && values.engagementType === "OUTSOURCING") {
    fields.push(
      "contractDuration",
      "expectedStartDate",
      "payrollSupport",
      "numberOfTalentsToBeManaged"
    );
  }
  return fields.some((field) => errors[field]);
};

const StepIcon = ({ number, active, complete, onClick }) => (
  <button
    type="button"
    onClick={complete ? onClick : undefined}
    className={`relative grid justify-items-center gap-2 bg-transparent border-0 p-0 ${
      complete ? "cursor-pointer" : "cursor-default"
    }`}
  >
    <span
      className={`relative z-[1] w-9 h-9 rounded-full border-2 grid place-items-center text-sm font-extrabold font-poppins ${
        active
          ? "border-secondary bg-secondary text-primary"
          : complete
          ? "border-primary bg-primary text-white"
          : "border-[#cfd6e5] bg-white text-[#949bad]"
      }`}
    >
      {complete ? "✓" : number}
    </span>
    <b
      className={`text-xs font-poppins font-normal ${
        active || complete ? "text-primary" : "text-[#949bad]"
      }`}
    >
      {STEPS[number - 1].label}
    </b>
  </button>
);

const HireRequestFormSection = () => {
  const { addToast } = useToasts();
  const [submitted, setSubmitted] = useState(false);
  const [step, setStep] = useState(0);

  const initialValues = {
    engagementType: "",
    workEmail: "",
    role: "",
    numberOfPeople: "",
    employmentType: "",
    successDescription: "",
    workLocation: "",
    startDate: "",
    contractDuration: "",
    expectedStartDate: "",
    payrollSupport: "",
    numberOfTalentsToBeManaged: "",
    performanceManagement: "",
    equipmentRequirement: "",
    complianceSupport: "",
    currency: "NGN",
    hasFixedBudget: false,
    minBudget: "",
    maxBudget: "",
    benefits: [],
  };

  return (
    <section
      className="bg-hire-tint-yellow text-default py-16 md:py-20 lg:py-24 px-4"
      id="request"
    >
      <div className="container mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-10 lg:gap-16">
          <div className="w-full lg:w-4/12 lg:sticky lg:top-28 lg:self-start">
            <Animate.Fade bottom>
              <p className="flex items-center font-poppins gap-3 text-primary text-sm font-extrabold tracking-widest uppercase mb-6">
                <span className="w-8 h-1 rounded-full bg-secondary inline-block"></span>
                Create a hiring request
              </p>

              <h2 className="text-4xl md:text-6xl font-poppins leading-tight  font-bold mb-6 tracking-[-0.03em]">
                Build Your Shortlist.
              </h2>

              <p className="text-gray text-base font-poppins md:text-lg leading-relaxed">
                Five focused steps give Utiva the context to match the right
                people and recommend the right workforce infrastructure.
              </p>

              <div className="border-l-4 border-secondary bg-white rounded-r-xl mt-9 p-5">
                <div className="flex items-center">
                  {[Avatar1, Avatar2, Avatar3].map((avatar, index) => (
                    <img
                      key={index}
                      src={avatar}
                      alt=""
                      className={`w-9 h-9 rounded-full object-cover border-2 border-white ${
                        index !== 0 ? "-ml-2" : ""
                      }`}
                    />
                  ))}
                </div>
                <strong className="text-primary font-poppins text-base block mt-3">
                  One request. Real hiring support.
                </strong>
                <span className="text-gray font-poppins text-sm block mt-1">
                  Talent access, hiring and post-hire support connected from the
                  start.
                </span>
              </div>

              <div className="flex items-start gap-4 mt-6 pt-6 border-t border-hire-line">
                <span className="w-8 h-8 font-poppins rounded-full bg-primary-light text-primary flex items-center justify-center font-extrabold flex-shrink-0">
                  ✓
                </span>
                <p className="text-gray font-poppins text-xs leading-relaxed m-0">
                  <strong className="text-default text-sm">
                    Clear and confidential
                  </strong>
                  <br />
                  Your information is used only to prepare your hiring
                  conversation.
                </p>
              </div>
            </Animate.Fade>
          </div>

          <div className="w-full lg:w-7/12">
            <Animate.Fade bottom>
              <div className="bg-white text-default rounded-3xl border border-[#e4d3a7] shadow-course-feature-lg p-5 md:p-7 min-h-[650px]">
                {submitted ? (
                  <div className="text-center py-10">
                    <span className="w-14 h-14 mx-auto rounded-full bg-primary-light text-primary flex items-center justify-center text-2xl font-extrabold mb-5">
                      ✓
                    </span>
                    <h3 className="text-primary text-2xl font-poppins font-semibold mb-3">
                      Request received
                    </h3>
                    <p className="text-gray text-base font-poppins leading-relaxed max-w-sm mx-auto">
                      Thanks for sharing your hiring needs. A hiring consultant
                      will reach out within one business day to continue the
                      conversation.
                    </p>
                  </div>
                ) : (
                  <Formik
                    initialValues={initialValues}
                    validate={validate}
                    onSubmit={async (values, { setSubmitting }) => {
                      const isOutsourcing =
                        values.engagementType === "OUTSOURCING";
                      const salaryType = values.hasFixedBudget
                        ? "FIXED"
                        : "RANGE";

                      const payload = {
                        engagementType: values.engagementType,
                        workEmail: values.workEmail,
                        role: values.role,
                        numberOfPeople: parseNumberOfPeople(
                          values.numberOfPeople
                        ),
                        employmentType: values.employmentType,
                        successDescription:
                          values.successDescription || undefined,
                        workLocation: values.workLocation,
                        startDate: values.startDate,
                        salaryType,
                        currency: values.currency,
                        minBudget: Number(values.minBudget),
                        ...(salaryType === "RANGE" && {
                          maxBudget: Number(values.maxBudget),
                        }),
                        benefits: values.benefits,
                        ...(isOutsourcing && {
                          contractDuration: values.contractDuration,
                          expectedStartDate: values.expectedStartDate
                            ? new Date(values.expectedStartDate).toISOString()
                            : undefined,
                          payrollSupport: values.payrollSupport,
                          numberOfTalentsToBeManaged: values.numberOfTalentsToBeManaged
                            ? Number(values.numberOfTalentsToBeManaged)
                            : undefined,
                          performanceManagement:
                            values.performanceManagement || undefined,
                          equipmentRequirement:
                            values.equipmentRequirement || undefined,
                          complianceSupport:
                            values.complianceSupport || undefined,
                        }),
                      };

                      try {
                        await submitHiringRequestApi(payload);
                        addToast("Hiring request submitted successfully!", {
                          appearance: "success",
                        });
                        setSubmitted(true);
                      } catch (error) {
                        console.log(error);
                        triggerError(addToast, error);
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleSubmit,
                      handleBlur,
                      setFieldValue,
                      setTouched,
                      isSubmitting,
                    }) => {
                      const isOutsourcing =
                        values.engagementType === "OUTSOURCING";

                      const currencySymbol = values.currency?.startsWith("USD")
                        ? "$"
                        : "₦";

                      const toggleBenefit = (benefit) => {
                        const next = values.benefits.includes(benefit)
                          ? values.benefits.filter((b) => b !== benefit)
                          : [...values.benefits, benefit];
                        setFieldValue("benefits", next);
                      };

                      const isLastStep = step === STEPS.length - 1;
                      const stepInvalid = stepHasErrors(step, values, errors);

                      const goNext = () => {
                        const fields = [...STEP_FIELDS[step]];
                        if (
                          step === 4 &&
                          values.engagementType === "OUTSOURCING"
                        ) {
                          fields.push(
                            "contractDuration",
                            "expectedStartDate",
                            "payrollSupport",
                            "numberOfTalentsToBeManaged"
                          );
                        }
                        const touchedPatch = {};
                        fields.forEach((f) => (touchedPatch[f] = true));
                        setTouched({ ...touched, ...touchedPatch });
                        if (stepInvalid) return;
                        setStep((s) => Math.min(s + 1, STEPS.length - 1));
                      };

                      const goBack = () => setStep((s) => Math.max(s - 1, 0));

                      return (
                        <form
                          onSubmit={
                            isLastStep
                              ? handleSubmit
                              : (e) => {
                                  e.preventDefault();
                                  goNext();
                                }
                          }
                          className="hire-form-fields"
                        >
                          <div className="border-b border-hire-line mb-8 pb-6">
                            <div className="grid grid-cols-5">
                              {STEPS.map((s, index) => (
                                <div
                                  key={s.key}
                                  className="relative flex justify-center"
                                >
                                  {index !== 0 && (
                                    <span
                                      className={`absolute top-[18px] right-1/2 w-full h-0.5 ${
                                        index <= step
                                          ? "bg-primary"
                                          : "bg-[#dde3ef]"
                                      }`}
                                    />
                                  )}
                                  <StepIcon
                                    number={index + 1}
                                    active={index === step}
                                    complete={index < step}
                                    onClick={() => setStep(index)}
                                  />
                                </div>
                              ))}
                            </div>
                            <p className="text-center text-[#8a5a00] font-poppins text-xs font-extrabold tracking-widest uppercase mt-4 mb-0">
                              Step {step + 1} of {STEPS.length}
                            </p>
                          </div>

                          <div className="min-h-[400px]">
                            {step === 0 && (
                              <div>
                                <div className="mb-8">
                                  <p className="text-base font-bold text-default mb-1">
                                    Select the recruitment engagement type *
                                  </p>
                                  <p className="text-sm text-gray mb-3">
                                    Choose the level of hiring and ongoing
                                    talent support you need.
                                  </p>
                                  <div className="grid grid-cols-1 gap-3">
                                    {ENGAGEMENT_TYPES.map((option) => (
                                      <RadioOption
                                        key={option.value}
                                        value={option.value}
                                        label={option.label}
                                        description={option.description}
                                        icon={option.icon}
                                        checked={
                                          values.engagementType === option.value
                                        }
                                        onChange={handleChange}
                                      />
                                    ))}
                                  </div>
                                  <span className="text-sm text-red-600">
                                    {errors.engagementType &&
                                      touched.engagementType &&
                                      errors.engagementType}
                                  </span>
                                </div>

                                <div className="w-full mb-6">
                                  <FieldLabel>Your work email *</FieldLabel>
                                  <InputGroup
                                    type="email"
                                    placeholder="you@company.com"
                                    name="workEmail"
                                    value={values.workEmail}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={
                                      errors.workEmail &&
                                      touched.workEmail &&
                                      errors.workEmail
                                    }
                                  />
                                </div>

                                <div className="w-full mb-6">
                                  <FieldLabel>
                                    What role are you hiring for? *
                                  </FieldLabel>
                                  <InputGroup
                                    type="text"
                                    placeholder="e.g. Senior Data Engineer"
                                    name="role"
                                    value={values.role}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={
                                      errors.role && touched.role && errors.role
                                    }
                                  />
                                </div>

                                <div className="w-full">
                                  <FieldLabel>
                                    Select the employment type *
                                  </FieldLabel>
                                  <SelectGroup
                                    title="employment type"
                                    name="employmentType"
                                    value={values.employmentType}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={
                                      errors.employmentType &&
                                      touched.employmentType &&
                                      errors.employmentType
                                    }
                                    options={EMPLOYMENT_TYPE_OPTIONS.map(
                                      (option) => (
                                        <option key={option} value={option}>
                                          {option}
                                        </option>
                                      )
                                    )}
                                  />
                                </div>
                              </div>
                            )}

                            {step === 1 && (
                              <div>
                                <div className="w-full mb-6">
                                  <FieldLabel>How many people? *</FieldLabel>
                                  <SelectGroup
                                    title=""
                                    name="numberOfPeople"
                                    value={values.numberOfPeople}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={
                                      errors.numberOfPeople &&
                                      touched.numberOfPeople &&
                                      errors.numberOfPeople
                                    }
                                    options={NUMBER_OF_PEOPLE_OPTIONS.map(
                                      (option) => (
                                        <option key={option} value={option}>
                                          {option}
                                        </option>
                                      )
                                    )}
                                  />
                                </div>

                                <div className="w-full">
                                  <FieldLabel>
                                    What would make this hire successful?
                                  </FieldLabel>
                                  <TextAreaGroup
                                    placeholder="Tell us about the team, the work, and what a great first six months would look like…"
                                    name="successDescription"
                                    value={values.successDescription}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    rows="4"
                                  />
                                </div>
                              </div>
                            )}

                            {step === 2 && (
                              <div>
                                <div className="w-full mb-6">
                                  <FieldLabel>
                                    Where can they work? *
                                  </FieldLabel>
                                  <SelectGroup
                                    title=""
                                    name="workLocation"
                                    value={values.workLocation}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={
                                      errors.workLocation &&
                                      touched.workLocation &&
                                      errors.workLocation
                                    }
                                    options={WORK_LOCATION_OPTIONS.map(
                                      (option) => (
                                        <option key={option} value={option}>
                                          {option}
                                        </option>
                                      )
                                    )}
                                  />
                                </div>

                                <div className="w-full">
                                  <FieldLabel>
                                    When do you need them? *
                                  </FieldLabel>
                                  <SelectGroup
                                    title=""
                                    name="startDate"
                                    value={values.startDate}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={
                                      errors.startDate &&
                                      touched.startDate &&
                                      errors.startDate
                                    }
                                    options={START_DATE_OPTIONS.map(
                                      (option) => (
                                        <option key={option} value={option}>
                                          {option}
                                        </option>
                                      )
                                    )}
                                  />
                                </div>
                              </div>
                            )}

                            {step === 3 && (
                              <div>
                                <p className="text-base font-bold text-default mb-1">
                                  Enter the approved monthly salary budget *
                                </p>
                                <p className="text-sm text-gray mb-4">
                                  Provide the approved gross monthly salary
                                  range for one employee.
                                </p>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 items-start mb-4">
                                  <div className="w-full">
                                    <FieldLabel>Currency</FieldLabel>
                                    <SelectGroup
                                      title=""
                                      name="currency"
                                      value={values.currency}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={
                                        errors.currency &&
                                        touched.currency &&
                                        errors.currency
                                      }
                                      options={CURRENCY_OPTIONS.map(
                                        (option) => (
                                          <option
                                            key={option.value}
                                            value={option.value}
                                          >
                                            {option.label}
                                          </option>
                                        )
                                      )}
                                    />
                                  </div>

                                  <div className="w-full mt-6 sm:mt-8">
                                    <CheckboxField
                                      name="hasFixedBudget"
                                      checked={values.hasFixedBudget}
                                      onChange={(e) =>
                                        setFieldValue(
                                          "hasFixedBudget",
                                          e.target.checked
                                        )
                                      }
                                      label="I have a fixed monthly budget"
                                    />
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 mb-6">
                                  <div className="w-full">
                                    <FieldLabel>
                                      Minimum monthly budget *
                                    </FieldLabel>
                                    <MoneyInput
                                      symbol={currencySymbol}
                                      placeholder="500,000"
                                      name="minBudget"
                                      value={values.minBudget}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={
                                        errors.minBudget &&
                                        touched.minBudget &&
                                        errors.minBudget
                                      }
                                    />
                                  </div>

                                  <div className="w-full">
                                    <FieldLabel>
                                      Maximum monthly budget *
                                    </FieldLabel>
                                    <MoneyInput
                                      symbol={currencySymbol}
                                      placeholder="800,000"
                                      name="maxBudget"
                                      value={values.maxBudget}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={
                                        errors.maxBudget &&
                                        touched.maxBudget &&
                                        errors.maxBudget
                                      }
                                    />
                                  </div>
                                </div>

                                <SectionHeading>Benefits</SectionHeading>
                                <p className="text-sm text-gray my-3">
                                  Select all that apply.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {BENEFIT_OPTIONS.map((benefit) => (
                                    <CheckOption
                                      key={benefit}
                                      name="benefits"
                                      value={benefit}
                                      label={benefit}
                                      checked={values.benefits.includes(
                                        benefit
                                      )}
                                      onChange={() => toggleBenefit(benefit)}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}

                            {step === 4 && (
                              <div>
                                {isOutsourcing ? (
                                  <div
                                    className="rounded-2xl border p-4 sm:p-5"
                                    style={{
                                      backgroundColor: GOLD_TINT_BG,
                                      borderColor: GOLD_TINT_BORDER,
                                    }}
                                  >
                                    <SectionHeading tone="secondary">
                                      Outsourcing details
                                    </SectionHeading>
                                    <p className="text-sm text-gray mb-4">
                                      Help us shape the right management
                                      support.
                                    </p>
                                    <div
                                      className="h-px mb-5"
                                      style={{
                                        backgroundColor: GOLD_TINT_BORDER,
                                      }}
                                    ></div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                                      <div className="w-full mb-6">
                                        <FieldLabel>
                                          Preferred contract duration *
                                        </FieldLabel>
                                        <SelectGroup
                                          title=""
                                          name="contractDuration"
                                          value={values.contractDuration}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            errors.contractDuration &&
                                            touched.contractDuration &&
                                            errors.contractDuration
                                          }
                                          options={CONTRACT_DURATION_OPTIONS.map(
                                            (option) => (
                                              <option
                                                key={option}
                                                value={option}
                                              >
                                                {option}
                                              </option>
                                            )
                                          )}
                                        />
                                      </div>

                                      <div className="w-full mb-6">
                                        <FieldLabel>
                                          Expected start date *
                                        </FieldLabel>
                                        <InputGroup
                                          type="date"
                                          name="expectedStartDate"
                                          value={values.expectedStartDate}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            errors.expectedStartDate &&
                                            touched.expectedStartDate &&
                                            errors.expectedStartDate
                                          }
                                        />
                                      </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                                      <div className="w-full mb-6">
                                        <FieldLabel>
                                          Payroll support requirement *
                                        </FieldLabel>
                                        <SelectGroup
                                          title=""
                                          name="payrollSupport"
                                          value={values.payrollSupport}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            errors.payrollSupport &&
                                            touched.payrollSupport &&
                                            errors.payrollSupport
                                          }
                                          options={YES_NO_OPTIONS.map(
                                            (option) => (
                                              <option
                                                key={option}
                                                value={option}
                                              >
                                                {option}
                                              </option>
                                            )
                                          )}
                                        />
                                      </div>

                                      <div className="w-full mb-6">
                                        <FieldLabel>
                                          Number of talents to be managed *
                                        </FieldLabel>
                                        <InputGroup
                                          type="number"
                                          min="1"
                                          placeholder="1"
                                          name="numberOfTalentsToBeManaged"
                                          value={
                                            values.numberOfTalentsToBeManaged
                                          }
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            errors.numberOfTalentsToBeManaged &&
                                            touched.numberOfTalentsToBeManaged &&
                                            errors.numberOfTalentsToBeManaged
                                          }
                                        />
                                      </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4">
                                      <div className="w-full mb-2">
                                        <FieldLabel>
                                          Performance management
                                        </FieldLabel>
                                        <SelectGroup
                                          title=""
                                          name="performanceManagement"
                                          value={values.performanceManagement}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          options={YES_NO_OPTIONS.map(
                                            (option) => (
                                              <option
                                                key={option}
                                                value={option}
                                              >
                                                {option}
                                              </option>
                                            )
                                          )}
                                        />
                                      </div>

                                      <div className="w-full mb-2">
                                        <FieldLabel>
                                          Equipment requirement
                                        </FieldLabel>
                                        <SelectGroup
                                          title=""
                                          name="equipmentRequirement"
                                          value={values.equipmentRequirement}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          options={EQUIPMENT_REQUIREMENT_OPTIONS.map(
                                            (option) => (
                                              <option
                                                key={option}
                                                value={option}
                                              >
                                                {option}
                                              </option>
                                            )
                                          )}
                                        />
                                      </div>

                                      <div className="w-full mb-2">
                                        <FieldLabel>
                                          Compliance / employment support
                                        </FieldLabel>
                                        <SelectGroup
                                          title=""
                                          name="complianceSupport"
                                          value={values.complianceSupport}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          options={YES_NO_OPTIONS.map(
                                            (option) => (
                                              <option
                                                key={option}
                                                value={option}
                                              >
                                                {option}
                                              </option>
                                            )
                                          )}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="rounded-2xl border border-hire-line bg-hire-tint-blue p-5 sm:p-6">
                                    <SectionHeading>
                                      Workforce infrastructure
                                    </SectionHeading>
                                    <p className="text-sm text-gray mt-4 leading-relaxed">
                                      For a one-time hire, Utiva sources and
                                      shortlists the talent — your team takes
                                      over onboarding and management directly.
                                      You&apos;ll get access to Manage & Pay
                                      tools whenever you need contracts,
                                      compliance or payroll support later.
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="flex items-center justify-between gap-5 border-t border-hire-line mt-8 pt-6">
                            {step > 0 ? (
                              <button
                                type="button"
                                onClick={goBack}
                                className="text-primary font-poppins font-bold text-sm bg-transparent border-0 px-3 py-2.5"
                              >
                                Back
                              </button>
                            ) : (
                              <span />
                            )}

                            {isLastStep ? (
                              <button
                                type="submit"
                                disabled={isSubmitting}
                                className="min-w-[180px] flex items-center justify-center gap-3 bg-primary text-secondary font-poppins font-extrabold text-base rounded-xl h-[52px] px-5 py-3.5 disabled:opacity-60 focus:outline-none"
                              >
                                {isSubmitting ? (
                                  "Submitting..."
                                ) : (
                                  <>
                                    Request my shortlist{" "}
                                    <ArrowUpRight className="w-3.5 h-3.5 inline-block" />
                                  </>
                                )}
                              </button>
                            ) : (
                              <button
                                type="submit"
                                disabled={stepInvalid}
                                className="min-w-[180px] flex items-center justify-center gap-3 bg-primary text-secondary font-poppins font-extrabold text-base rounded-xl h-[52px] px-5 py-3.5 focus:outline-none disabled:bg-[#c6cbda] disabled:text-white disabled:cursor-not-allowed"
                              >
                                Continue <span aria-hidden="true">→</span>
                              </button>
                            )}
                          </div>

                          <p className="text-sm text-gray mt-4 text-right leading-relaxed">
                            Clear, confidential and reviewed by a real Utiva
                            hiring consultant.
                          </p>
                        </form>
                      );
                    }}
                  </Formik>
                )}
              </div>
            </Animate.Fade>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HireRequestFormSection;
